import { SelectOptionType } from '@models/common';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import { floor, round, ceil } from 'lodash';

/**
 * 將數字格式化為帶有指定小數位數的本地化字符串表示。
 *
 * @param number - 要格式化的數字。
 * @param digits - 小數點後至少顯示的位數。如果未指定，預設為3。
 * @returns 數字的字符串表示形式，根據運行時的默認地區設置進行本地化，並至少包含指定的小數位數。
 *
 * @example
 * // 將12345.6格式化為"12,345.600"
 * console.log(formatNumberWithDigits(12345.6));
 *
 * @example
 * // 在指定最小小數位數為2的情況下，將12345.678格式化為"12,345.678"
 * console.log(formatNumberWithDigits(12345.678, 2));
 */
export enum ERounding {
  ROUND = 'ROUND',
  FLOOR = 'FLOOR',
  CEIL = 'CEIL',
}
export function formatNumberWithDigits(
  number: number | string,
  digits = 3,
  rounding = ERounding.ROUND,
  roundFunc = round,
  floorFunc = floor,
  ceilFunc = ceil,
): string {
  if (number === null || number === undefined) return (0).toFixed(digits);
  const roundingMap: Record<string, (...a: any[]) => any> = {
    [ERounding.ROUND]: roundFunc,
    [ERounding.FLOOR]: floorFunc,
    [ERounding.CEIL]: ceilFunc,
  };
  let formattedNumber = number;
  if (typeof number === 'string') {
    formattedNumber = Number(number);
  }

  return roundingMap[rounding](formattedNumber, digits).toLocaleString(
    undefined,
    {
      minimumFractionDigits: digits,
    },
  );
}

/**
 * 將數字格式化為帶有千分位逗點的本地化字符串表示。
 * 不處理小數位數！如果原數據有小數位數，則會保留小數位數。
 * 這個 function 可以安全地處理 null、undefined 和 NaN （會將它們變為 0）。
 *
 * @param {number} num - The number to format.
 * @returns {string} - The formatted number as a string.
 *
 * @example
 * formatNumberWithCommas(11); // "11"
 * formatNumberWithCommas(0.234); // "0.234"
 * formatNumberWithCommas(1234.56789); // "1,234.56789"
 * formatNumberWithCommas(6666666); // "6,666,666"
 * formatNumberWithCommas(0); // "0"
 * */
export function formatNumberWithCommas(num: number | string): string {
  const number = Number(num);
  if (number === 0 || Number.isNaN(number)) return '0';
  const [integerPart, decimalPart] = number.toString().split('.');
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ',',
  );

  return decimalPart
    ? `${formattedIntegerPart}.${decimalPart}`
    : formattedIntegerPart;
}

export function generateYearRangeOptions(
  start: number = 2016,
  end: number = dayjs().year(),
): SelectOptionType[] {
  const yearRangeOptions = [];
  let newStart = start;
  let newEnd = end;

  // 如果開始年份大於結束年份，則兩者對調
  if (newStart > newEnd) {
    [newStart, newEnd] = [newEnd, newStart];
  }

  if (newStart <= 0) {
    newStart = 2016;
  }

  for (let year = newStart; year <= newEnd; year += 1) {
    yearRangeOptions.push({
      label: year.toString(),
      value: year.toString(),
    });
  }
  return yearRangeOptions;
}

export function enumToSelectOption(
  enumObj: Record<string, string>,
): SelectOptionType[] {
  return Object.keys(enumObj).map((key) => ({
    value: key,
    label: enumObj[key],
  }));
}

/**
 * 將秒數轉換為 "hh:mm:ss" 格式的時間字串
 * @param seconds - 以秒為單位的數字
 * @returns 格式化後的時間字串
 */
export function formatDuration(seconds: number): string {
  if (typeof seconds !== 'number' || seconds < 0) {
    return '00:00:00';
  }

  // 計算小時數，3600秒為1小時
  const hours = Math.floor(seconds / 3600);

  // 計算分鐘數，先除以3600取餘數，再除以60
  const minutes = Math.floor((seconds % 3600) / 60);

  // 計算剩餘的秒數，除以60取餘數
  const remainingSeconds = seconds % 60;

  // 將每個值轉換為字串並用0填充
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  const secondsStr = String(remainingSeconds).padStart(2, '0');

  // 組合成所需格式的字串
  return `${hoursStr}:${minutesStr}:${secondsStr}`;
}

export function timeDifference(startTime: string, endTime: string) {
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  const difference = dayjs.duration(end.diff(start));

  const hours = difference.hours().toString().padStart(2, '0');
  const minutes = difference.minutes().toString().padStart(2, '0');
  const seconds = difference.seconds().toString().padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
}

export function downloadFile(res: any) {
  const blob = res.data;

  const contentDisposition = res.headers['content-disposition'];

  let fileName = 'download.xlsx';

  // Extract the file name from the Content-Disposition header if it exists
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (fileNameMatch.length === 2) {
      const [, encodedFileName] = fileNameMatch;
      fileName = decodeURIComponent(encodedFileName);
    }
  }

  fileDownload(blob, fileName);
}

export function isLeapYear(year: number) {
  const yearNumber = Number(year);
  if (Number.isNaN(yearNumber)) return false;

  return (
    (yearNumber % 4 === 0 && yearNumber % 100 !== 0) || yearNumber % 400 === 0
  );
}
